import type { SelectInternalTerminal } from '@workspace/db/extra'
import { useEffect, useState } from 'react'
import { apiErrorMessage } from '../../../features/locations/api-errors'
import { useLocation } from '../../../features/locations/use-location'
import {
  cashDrawerError,
  useAssignTerminal,
  useCashDrawerConfig,
  useLocationTerminals,
  usePrinterConfig,
  useSetupCashDrawer,
  useSetupReceiptPrinter,
  useTerminalSearch,
  useUnassignCashDrawer,
  useUnassignReceiptPrinter,
  useUnassignTerminal,
} from '../../../features/locations/use-pos-terminals'
import { useIdOrCode } from '../../use-id-or-code'
import { ActionRow, Btn, ConfirmDialog, Dialog, Field, Inline, input, PageHeader, Select, Tag, th } from './controls'
import { mono, Note, Section, SkeletonRows, Status, serif } from './ui'

const COOLDOWN_S = 90

/** Client-side stand-in for v1's 90s per-workstation KV cooldown (the server lock is not built in v2). */
function useCooldown() {
  const [until, setUntil] = useState(0)
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    if (until <= Date.now()) return
    const t = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(t)
  }, [until])
  return {
    left: Math.max(0, Math.ceil((until - now) / 1000)),
    start: () => {
      setNow(Date.now())
      setUntil(Date.now() + COOLDOWN_S * 1000)
    },
  }
}

function AssignDialog({
  idOrCode,
  storeCode,
  banner,
  assignedIds,
  onClose,
}: {
  idOrCode: string
  storeCode: string
  banner: string
  assignedIds: Set<string>
  onClose: () => void
}) {
  const [draft, setDraft] = useState('')
  const [q, setQ] = useState('')
  const search = useTerminalSearch(banner, q)
  const assign = useAssignTerminal()
  const hits = (search.data ?? []).filter((t) => !assignedIds.has(t.id))

  return (
    <Dialog
      open
      eyebrow="Assignment"
      title="Assign a terminal"
      onClose={onClose}
      footer={<Btn onClick={onClose}>Done</Btn>}
    >
      <form
        className="flex items-end gap-4"
        onSubmit={(e) => {
          e.preventDefault()
          setQ(draft.trim())
        }}
      >
        <div className="flex-1">
          <Field label="Terminal name">
            <input
              className={input}
              placeholder="Search by terminal name"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              style={mono}
            />
          </Field>
        </div>
        <Btn type="submit" variant="primary" disabled={draft.trim() === ''}>
          Search
        </Btn>
      </form>
      {search.isFetching ? <Inline tone="muted">Searching…</Inline> : null}
      {search.error ? <Inline tone="bad">{apiErrorMessage(search.error)}</Inline> : null}
      {q && !search.isFetching && hits.length === 0 ? (
        <Inline tone="muted">No unassigned terminal matches.</Inline>
      ) : null}
      <ul className="m-0 list-none border-t border-[var(--color-border)] p-0 empty:hidden">
        {hits.map((t) => (
          <li
            key={t.id}
            className="flex items-baseline justify-between gap-4 border-b border-[var(--color-border)] py-2.5"
          >
            <span className="min-w-0 truncate text-[15px]" style={serif}>
              {t.name}
              <span className="ml-2 text-[12px] text-[var(--color-muted)]" style={mono}>
                {[t.model, t.serialNumber].filter(Boolean).join(' · ')}
              </span>
            </span>
            <Btn disabled={assign.isPending} onClick={() => assign.mutate({ idOrCode, terminalId: t.id, storeCode })}>
              Assign
            </Btn>
          </li>
        ))}
      </ul>
      {assign.error ? <Inline tone="bad">{apiErrorMessage(assign.error)}</Inline> : null}
      {assign.isSuccess ? <Inline tone="ok">Terminal assigned.</Inline> : null}
    </Dialog>
  )
}

const PRINTER_MODELS: [string, string][] = [
  ['TM-H6000IV', 'TM-H6000IV (USB)'],
  ['TM-88V', 'TM-88V (Network)'],
  ['Toshiba6145', 'Toshiba 6145'],
]
const LOGICAL = ['CashDrawer1', 'CashDrawer2', 'CashDrawer3', 'CashDrawer4'] as const

function PeripheralsDialog({
  code,
  terminal,
  onClose,
}: {
  code: string
  terminal: SelectInternalTerminal
  onClose: () => void
}) {
  const ws = terminal.workstationId ?? null
  const printer = usePrinterConfig(code, ws)
  const drawer = useCashDrawerConfig(code, ws)
  const setupPrinter = useSetupReceiptPrinter()
  const unassignPrinter = useUnassignReceiptPrinter()
  const setupDrawer = useSetupCashDrawer()
  const unassignDrawer = useUnassignCashDrawer()
  const cooldown = useCooldown()

  const [model, setModel] = useState('')
  const [mac, setMac] = useState('')
  const [size, setSize] = useState<'' | 'large' | 'small'>('')
  const [ptype, setPtype] = useState<'' | 'epson' | 'toshiba'>('')
  const [logical, setLogical] = useState<(typeof LOGICAL)[number]>('CashDrawer1')
  const [smallOk, setSmallOk] = useState(false)

  const locked = cooldown.left > 0
  const printerValid = model !== '' && (model !== 'TM-88V' || mac.trim() !== '')
  const drawerRuleError = size ? cashDrawerError({ drawerSize: size, printerType: ptype }) : null
  const drawerValid = size !== '' && !drawerRuleError && (size !== 'small' || smallOk)
  const title = `Peripherals for ${terminal.name}`

  if (!ws)
    return (
      <Dialog open eyebrow="Peripherals" title={title} onClose={onClose} footer={<Btn onClick={onClose}>Close</Btn>}>
        <p className="text-[15px] italic leading-[1.5] text-[var(--color-muted)]" style={serif}>
          This terminal has no workstation number yet, so a receipt printer or cash drawer cannot be set up. Register it
          as a workstation first.
        </p>
      </Dialog>
    )

  const legend = 'text-[12px] font-semibold uppercase tracking-[0.12em] text-[var(--color-muted)]'

  return (
    <Dialog open eyebrow="Peripherals" title={title} onClose={onClose} footer={<Btn onClick={onClose}>Close</Btn>}>
      <p className="text-[14px] italic text-[var(--color-muted)]" style={serif}>
        Workstation <span style={mono}>{ws}</span>. Changes apply asynchronously
        {locked ? `; the next change unlocks in ${cooldown.left}s` : ''}.
      </p>

      <section className="grid gap-3 border-t border-[var(--color-border)] pt-3">
        <h3 className={legend}>Receipt printer</h3>
        {printer.isLoading ? (
          <Inline tone="muted">Loading…</Inline>
        ) : printer.data?.hasPrinter ? (
          <div className="flex items-baseline justify-between gap-3">
            <span className="text-[15px]" style={serif}>
              <Tag tone="ok">{printer.data.printerModel ?? 'Configured'}</Tag>
              {printer.data.printerIp ? (
                <span className="ml-2 text-[12px] text-[var(--color-muted)]" style={mono}>
                  {printer.data.printerIp}
                </span>
              ) : null}
            </span>
            <Btn
              variant="danger"
              disabled={locked || unassignPrinter.isPending}
              onClick={() => unassignPrinter.mutate({ code, workstationId: ws }, { onSuccess: cooldown.start })}
            >
              {unassignPrinter.isPending ? 'Removing…' : 'Unassign'}
            </Btn>
          </div>
        ) : (
          <>
            <Select
              label="Printer model"
              value={model}
              onChange={setModel}
              options={PRINTER_MODELS}
              placeholder="Select a model"
            />
            {model === 'TM-88V' ? (
              <Field label="Printer MAC">
                <input className={input} value={mac} onChange={(e) => setMac(e.target.value)} style={mono} />
              </Field>
            ) : null}
            <div>
              <Btn
                variant="primary"
                disabled={!printerValid || locked || setupPrinter.isPending}
                onClick={() =>
                  setupPrinter.mutate(
                    {
                      code,
                      workstationId: ws,
                      printerModel: model,
                      printerMac: model === 'TM-88V' ? mac.trim() : undefined,
                    },
                    { onSuccess: cooldown.start },
                  )
                }
              >
                {setupPrinter.isPending ? 'Setting up…' : 'Set up printer'}
              </Btn>
            </div>
          </>
        )}
        {printer.error ? <Inline tone="bad">{apiErrorMessage(printer.error)}</Inline> : null}
        {setupPrinter.error ? <Inline tone="bad">{apiErrorMessage(setupPrinter.error)}</Inline> : null}
        {unassignPrinter.error ? <Inline tone="bad">{apiErrorMessage(unassignPrinter.error)}</Inline> : null}
      </section>

      <section className="grid gap-3 border-t border-[var(--color-border)] pt-3">
        <h3 className={legend}>Cash drawer</h3>
        {drawer.isLoading ? (
          <Inline tone="muted">Loading…</Inline>
        ) : drawer.data?.hasCashDrawer ? (
          <div className="flex items-baseline justify-between gap-3">
            <Tag tone="ok">
              {`${drawer.data.drawerSize ?? 'Configured'}${drawer.data.logicalName ? ` · ${drawer.data.logicalName}` : ''}`}
            </Tag>
            <Btn
              variant="danger"
              disabled={locked || unassignDrawer.isPending}
              onClick={() => unassignDrawer.mutate({ code, workstationId: ws }, { onSuccess: cooldown.start })}
            >
              {unassignDrawer.isPending ? 'Removing…' : 'Unassign'}
            </Btn>
          </div>
        ) : (
          <>
            <Select
              label="Drawer size"
              value={size}
              onChange={(v) => setSize(v as '' | 'large' | 'small')}
              options={[
                ['large', 'Large'],
                ['small', 'Small'],
              ]}
              placeholder="Select a size"
            />
            {size === 'large' ? (
              <>
                <Select
                  label="Printer type"
                  value={ptype}
                  onChange={(v) => setPtype(v as '' | 'epson' | 'toshiba')}
                  options={[
                    ['epson', 'Epson'],
                    ['toshiba', 'Toshiba'],
                  ]}
                  placeholder="Printer type"
                />
                <Select
                  label="Logical name"
                  value={logical}
                  onChange={(v) => setLogical(v as typeof logical)}
                  options={LOGICAL.map((n) => [n, n])}
                />
              </>
            ) : null}
            {size === 'small' ? (
              <label className="flex items-start gap-2 text-[14px] text-[var(--color-muted)]">
                <input
                  type="checkbox"
                  className="mt-1 accent-[var(--color-accent)]"
                  checked={smallOk}
                  onChange={(e) => setSmallOk(e.target.checked)}
                />
                I confirmed the device number is "3" in POS Control Center.
              </label>
            ) : null}
            {drawerRuleError ? <Inline tone="bad">{drawerRuleError}</Inline> : null}
            <div>
              <Btn
                variant="primary"
                disabled={!drawerValid || locked || setupDrawer.isPending}
                onClick={() =>
                  size &&
                  setupDrawer.mutate(
                    {
                      code,
                      workstationId: ws,
                      drawerSize: size,
                      printerType: size === 'large' && ptype ? ptype : undefined,
                      logicalName: size === 'large' ? logical : undefined,
                    },
                    { onSuccess: cooldown.start },
                  )
                }
              >
                {setupDrawer.isPending ? 'Setting up…' : 'Set up cash drawer'}
              </Btn>
            </div>
          </>
        )}
        {drawer.error ? <Inline tone="bad">{apiErrorMessage(drawer.error)}</Inline> : null}
        {setupDrawer.error ? <Inline tone="bad">{apiErrorMessage(setupDrawer.error)}</Inline> : null}
        {unassignDrawer.error ? <Inline tone="bad">{apiErrorMessage(unassignDrawer.error)}</Inline> : null}
      </section>
    </Dialog>
  )
}

const termLabel = (s: string | null) => (s === 'online' ? 'Online' : s === 'offline' ? 'Offline' : (s ?? 'Unknown'))

export function LedgerPosTerminals() {
  const idOrCode = useIdOrCode()
  const location = useLocation(idOrCode)
  const terminals = useLocationTerminals(idOrCode)
  const unassign = useUnassignTerminal()

  const [assignOpen, setAssignOpen] = useState(false)
  const [periph, setPeriph] = useState<SelectInternalTerminal | null>(null)
  const [removing, setRemoving] = useState<SelectInternalTerminal | null>(null)

  const header = (
    <PageHeader
      idOrCode={idOrCode}
      section="POS"
      page="Terminals"
      title="Payment terminals"
      standfirst="The card terminals assigned to this store, and the printers and drawers wired to them."
    />
  )

  if (location.isLoading || terminals.isLoading)
    return (
      <article>
        {header}
        <SkeletonRows />
      </article>
    )
  const loc = location.data
  if (location.error || terminals.error || !loc)
    return (
      <article>
        {header}
        <Note tone="error">
          The terminals could not be read: {(location.error ?? terminals.error)?.message ?? 'not found.'}
        </Note>
      </article>
    )

  const list = terminals.data ?? []
  const storeCode = loc.aptosStoreCode ?? loc.code ?? idOrCode
  // Printer/drawer `code` is passed bare (the location code), as v1's terminals route does.
  const posCode = loc.code ?? storeCode

  return (
    <article>
      {header}
      <ActionRow>
        <Btn variant="primary" onClick={() => setAssignOpen(true)}>
          Assign a terminal
        </Btn>
      </ActionRow>

      <Section
        title="Terminals"
        aside={<span className="tabular-nums text-[var(--color-muted)]">{list.length} assigned</span>}
      >
        {list.length === 0 ? (
          <Note>No terminals are assigned to this store.</Note>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px] border-collapse" aria-label="Terminals">
              <thead>
                <tr className="border-b border-[var(--color-border)]">
                  {['Terminal', 'Model', 'Status', 'Workstation', 'IP', 'Firmware', ''].map((h) => (
                    <th key={h || 'actions'} scope="col" className={th}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {list.map((t) => (
                  <tr key={t.id} className="h-11 border-b border-[var(--color-border)]">
                    <td className="pr-4 text-[15px] font-semibold" style={serif}>
                      {t.name}
                    </td>
                    <td className="pr-4 text-[14px] text-[var(--color-muted)]">{t.model ?? '—'}</td>
                    <td className="pr-4">
                      <Status label={termLabel(t.status)} />
                    </td>
                    <td className="pr-4 text-[13px]" style={mono}>
                      {t.workstationId ?? '—'}
                    </td>
                    <td className="pr-4 text-[13px]" style={mono}>
                      {t.ethernetIpAddress ?? t.wifiIpAddress ?? '—'}
                    </td>
                    <td className="pr-4 text-[12px] text-[var(--color-muted)]" style={mono}>
                      {t.firmwareVersion ?? '—'}
                    </td>
                    <td className="text-right">
                      <span className="inline-flex gap-4">
                        <Btn onClick={() => setPeriph(t)}>Peripherals</Btn>
                        <Btn variant="danger" onClick={() => setRemoving(t)}>
                          Unassign
                        </Btn>
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      {assignOpen ? (
        <AssignDialog
          idOrCode={idOrCode}
          storeCode={storeCode}
          banner={loc.banner ?? ''}
          assignedIds={new Set(list.map((t) => t.id))}
          onClose={() => setAssignOpen(false)}
        />
      ) : null}
      {periph ? <PeripheralsDialog code={posCode} terminal={periph} onClose={() => setPeriph(null)} /> : null}
      <ConfirmDialog
        open={removing !== null}
        danger
        title={`Unassign ${removing?.name ?? 'this terminal'}?`}
        body="The terminal leaves this store so it can be assigned elsewhere. Printer and cash drawer setup is left as it is."
        confirmLabel="Unassign"
        pending={unassign.isPending}
        error={unassign.error ? apiErrorMessage(unassign.error) : null}
        onConfirm={() =>
          removing && unassign.mutate({ idOrCode, terminalId: removing.id }, { onSuccess: () => setRemoving(null) })
        }
        onClose={() => {
          unassign.reset()
          setRemoving(null)
        }}
      />
    </article>
  )
}
